import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Building2, Users, HardHat, Wrench, FileText, AlertTriangle, XCircle, CheckCircle2, GraduationCap, Stethoscope, ClipboardCheck, TrendingUp, Briefcase, FileWarning } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const fmtMese = (k) => { const [y, m] = k.split("-"); return `${MESI[parseInt(m, 10) - 1]} ${y.slice(2)}`; };

function TrendChart({ data }) {
  const rows = data.map(d => ({ ...d, label: fmtMese(d.mese) }));
  const total = rows.reduce((s, r) => s + r.scaduti + r.in_scadenza, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="trend-chart">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider font-mono text-slate-500">Andamento scadenze</div>
          <div className="font-display font-semibold text-slate-900">Documenti in scadenza per mese</div>
        </div>
        <div className="text-sm text-slate-500">{total} documenti nel periodo</div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="scaduti" name="Scaduti" stackId="a" fill="#B91C1C" radius={[0, 0, 0, 0]} />
            <Bar dataKey="in_scadenza" name="In scadenza" stackId="a" fill="#D97706" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const KpiCard = ({ icon: Icon, label, value, tone = "slate", to }) => {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red: "bg-red-50 border-red-200 text-red-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  const inner = (
    <div className={`card-lift border rounded-lg p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider font-mono opacity-70">{label}</div>
          <div className="text-3xl font-display font-bold mt-2">{value}</div>
        </div>
        <div className="p-2 rounded-md bg-white/60"><Icon size={20} /></div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [missing, setMissing] = useState([]);
  const isStudio = user?.role === "studio";

  useEffect(() => {
    (async () => {
      const [{ data }, m] = await Promise.all([api.get("/dashboard/stats"), api.get("/missing")]);
      setStats(data); setMissing(m.data);
    })();
  }, []);

  if (!stats) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <header>
        <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">{isStudio ? "Area riservata" : "Panoramica"}</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{isStudio ? user.studio?.denominazione : "Dashboard"}</h1>
        <p className="text-slate-500 mt-1">{isStudio ? "Aziende, lavoratori e documentazione messi a disposizione dallo Studio Aragona." : "Stato completo della documentazione sicurezza gestita dallo studio."}</p>
      </header>

      <section>
        <h2 className="font-display font-semibold text-slate-900 mb-3">Anagrafiche</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {user?.role === "admin" && <KpiCard icon={Briefcase} label="Studi di Consulenza" value={stats.studios} to="/studi" />}
          <KpiCard icon={Building2} label={isStudio ? "Aziende assegnate" : "Aziende"} value={stats.companies} to="/aziende" />
          <KpiCard icon={Users} label="Lavoratori" value={stats.workers} to="/lavoratori" />
          <KpiCard icon={HardHat} label="Cantieri attivi" value={`${stats.sites_active}/${stats.sites_total}`} to="/cantieri" />
          <KpiCard icon={Wrench} label="Attrezzature" value={stats.equipment} to="/attrezzature" />
        </div>
      </section>

      <section>
        <h2 className="font-display font-semibold text-slate-900 mb-3">Documenti e scadenze</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiCard icon={FileText} label="Totale documenti" value={stats.documents} tone="blue" to="/scadenzario" />
          <KpiCard icon={CheckCircle2} label="Regolari" value={stats.valid} tone="green" to="/scadenzario?status=valid" />
          <KpiCard icon={AlertTriangle} label="In scadenza" value={stats.expiring} tone="amber" to="/scadenzario?status=expiring" />
          <KpiCard icon={XCircle} label="Scaduti" value={stats.expired} tone="red" to="/scadenzario?status=expired" />
          <KpiCard icon={FileWarning} label="Mancanti" value={stats.missing} tone="amber" />
        </div>
      </section>

      {missing.length > 0 && (
        <section data-testid="missing-overview">
          <h2 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><FileWarning size={18} /> Documenti obbligatori mancanti</h2>
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {missing.slice(0, 12).map(m => (
              <Link key={m.company_id} to={`/aziende/${m.company_id}`} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-sm">
                <div>
                  <div className="font-medium text-slate-900">{m.company_name}</div>
                  <div className="text-xs text-slate-500">{m.company.length > 0 && `Azienda: ${m.company.join(", ")}`}{m.company.length > 0 && m.workers.length > 0 && " · "}{m.workers.length > 0 && `${m.workers.length} lavoratori con documenti mancanti`}</div>
                </div>
                <span className="bg-amber-100 text-amber-900 rounded-full px-2.5 py-0.5 text-xs font-semibold">{m.total}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp size={18} /> Trend scadenze</h2>
        <TrendChart data={stats.trend || []} />
      </section>

      <section>
        <h2 className="font-display font-semibold text-slate-900 mb-3">Alert specifici</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard icon={GraduationCap} label="Formazione in scadenza" value={stats.formazione_expiring} tone="amber" />
          <KpiCard icon={Stethoscope} label="Idoneità sanitarie" value={stats.idoneita_expiring} tone="amber" />
          <KpiCard icon={ClipboardCheck} label="Verifiche attrezzature" value={stats.verifiche_expiring} tone="amber" />
        </div>
      </section>
    </div>
  );
}
